import { useTracking } from 'react-tracking';
import { RouterLink } from '@redux-first-router-link';
import { MobileFooterMenuWrapper, MobileFooterMenuItem } from './styled';
import {
  ROUTE_COMMUNITY_IDEA,
  ROUTE_CREATE_CRITERIA,
  ROUTE_DASHBOARD,
  ROUTE_DASHBOARD_WITH_EVENTS,
  ROUTE_DEPRECATED_PORTFOLIO,
  ROUTE_DEPRECATED_PORTFOLIO_EDIT,
  ROUTE_DEPRECATED_PORTFOLIO_EDIT_TXN,
  ROUTE_DEPRECATED_PORTFOLIO_HOME,
  ROUTE_DEPRECATED_PORTFOLIO_LIST,
  ROUTE_DEPRECATED_PORTFOLIO_RETURNS_REPORT,
  ROUTE_DEPRECATED_PORTFOLIO_SHARE,
  ROUTE_DEPRECATED_PUBLIC_PORTFOLIO,
  ROUTE_MY_PORTFOLIOS,
  ROUTE_DISCOVER_INVESTING_IDEAS,
  ROUTE_DISCOVER_SAMPLE_PORTFOLIOS,
  ROUTE_EDIT_EXPLORE_IDEA,
  ROUTE_EDIT_USER_IDEA,
  ROUTE_HOME,
  ROUTE_INVESTING_IDEA_DISCOVERY_PAGE,
  ROUTE_INVESTING_IDEA_MARKET,
  ROUTE_INVESTING_IDEA_MARKET_INDUSTRY,
  ROUTE_MARKET,
  ROUTE_MARKET_HOME,
  ROUTE_MARKET_SECTOR,
  ROUTE_MARKET_SECTOR_SECONDARY,
  ROUTE_MARKET_SECTOR_SECONDARY_TERTIARY,
  ROUTE_PORTFOLIO,
  ROUTE_PORTFOLIO_DEFAULT,
  ROUTE_PORTFOLIO_NEW,
  ROUTE_WATCHLIST,
} from '@/constants/routes';
import { useCompanyShareLink } from '@/hooks/useCompanyShareToken';
import useUser from '@/hooks/useUser';
import { useHomeRoute } from '@/hooks/useHomeRoute';
import { useTranslation } from '@/hooks/useTranslation';
import HomeOutline from '@simplywallst/ui-core/icons/HomeOutline';
import StocksOutline from '@simplywallst/ui-core/icons/StocksOutline';
import DiscoverOutline from '@simplywallst/ui-core/icons/DiscoverOutline';
import WatchlistOutline from '@simplywallst/ui-core/icons/WatchlistOutline';
import ScreenerOutline from '@simplywallst/ui-core/icons/ScreenerOutline';
import PortfolioOutline from '@simplywallst/ui-core/icons/PortfolioOutline';
import HomeSolid from '@simplywallst/ui-core/icons/HomeSolid';
import StocksSolid from '@simplywallst/ui-core/icons/StocksSolid';
import DiscoverSolid from '@simplywallst/ui-core/icons/DiscoverSolid';
import WatchlistSolid from '@simplywallst/ui-core/icons/WatchlistSolid';
import ScreenerSolid from '@simplywallst/ui-core/icons/ScreenerSolid';
import PortfolioSolid from '@simplywallst/ui-core/icons/PortfolioSolid';
import { useSelector } from 'react-redux';
import { getLocationType } from '@/redux/location/selectors';

const subRouteTypes = {
  [ROUTE_HOME]: [ROUTE_DASHBOARD, ROUTE_DASHBOARD_WITH_EVENTS],
  [ROUTE_MARKET_HOME]: [
    ROUTE_MARKET,
    ROUTE_MARKET_HOME,
    ROUTE_MARKET_SECTOR,
    ROUTE_MARKET_SECTOR_SECONDARY_TERTIARY,
    ROUTE_MARKET_SECTOR_SECONDARY,
  ],
  [ROUTE_DISCOVER_INVESTING_IDEAS]: [
    ROUTE_DISCOVER_INVESTING_IDEAS,
    ROUTE_DISCOVER_SAMPLE_PORTFOLIOS,
    ROUTE_INVESTING_IDEA_MARKET,
    ROUTE_INVESTING_IDEA_MARKET_INDUSTRY,
    ROUTE_INVESTING_IDEA_DISCOVERY_PAGE,
    ROUTE_COMMUNITY_IDEA,
    ROUTE_DEPRECATED_PUBLIC_PORTFOLIO,
    ROUTE_DEPRECATED_PORTFOLIO_SHARE,
  ],
  [ROUTE_WATCHLIST]: [ROUTE_WATCHLIST],
  [ROUTE_DEPRECATED_PORTFOLIO_LIST]: [
    ROUTE_PORTFOLIO,
    ROUTE_PORTFOLIO_NEW,
    ROUTE_PORTFOLIO_DEFAULT,
    ROUTE_DEPRECATED_PORTFOLIO_LIST,
    ROUTE_MY_PORTFOLIOS,
    ROUTE_DEPRECATED_PORTFOLIO,
    ROUTE_DEPRECATED_PORTFOLIO_EDIT,
    ROUTE_DEPRECATED_PORTFOLIO_HOME,
    ROUTE_DEPRECATED_PORTFOLIO_EDIT_TXN,
    ROUTE_DEPRECATED_PORTFOLIO_RETURNS_REPORT,
  ],
  [ROUTE_CREATE_CRITERIA]: [
    ROUTE_CREATE_CRITERIA,
    ROUTE_EDIT_USER_IDEA,
    ROUTE_EDIT_EXPLORE_IDEA,
  ],
};

const useSelected = () => {
  const type = useSelector(getLocationType);
  const isSelected = (currentRouteType: keyof typeof subRouteTypes) => {
    return subRouteTypes[currentRouteType]?.includes(type);
  };
  return isSelected;
};

export const MobileFooterMenu = () => {
  const { isSharedCompany } = useCompanyShareLink();
  const { data: user } = useUser();
  const { trackEvent } = useTracking();
  const homeRoute = useHomeRoute();
  const isSelected = useSelected();
  const t = useTranslation();

  const shouldHideFooterMenu = !user.loggedIn && isSharedCompany;
  if (shouldHideFooterMenu) return null;

  return (
    <MobileFooterMenuWrapper as="nav">
      <MobileFooterMenuItem
        column
        col={2}
        alignItem="center"
        justifyContent="flex-start"
        displayFlex
        selected={isSelected(ROUTE_HOME)}
      >
        <RouterLink
          inheritColor
          noUnderline
          to={{
            type: homeRoute,
          }}
          onClick={() => {
            trackEvent({
              type: 'track',
              subject: 'primaryNavMobile',
              modifier: 'Dashboard',
              action: 'click',
            });
          }}
        >
          <p>{isSelected(ROUTE_HOME) ? <HomeSolid /> : <HomeOutline />}</p>
          <span>{t('app.topbar.nav.dashboard')}</span>
        </RouterLink>
      </MobileFooterMenuItem>
      <MobileFooterMenuItem
        column
        col={2}
        alignItem="center"
        justifyContent="flex-start"
        displayFlex
        selected={isSelected(ROUTE_MARKET_HOME)}
      >
        <RouterLink
          inheritColor
          noUnderline
          to={{
            type: ROUTE_MARKET_HOME,
          }}
          onClick={() => {
            trackEvent({
              type: 'track',
              subject: 'primaryNavMobile',
              modifier: 'Markets',
              action: 'click',
            });
          }}
        >
          <p>
            {isSelected(ROUTE_MARKET_HOME) ? (
              <StocksSolid />
            ) : (
              <StocksOutline />
            )}
          </p>
          <span>{t('app.topbar.nav.markets')}</span>
        </RouterLink>
      </MobileFooterMenuItem>
      <MobileFooterMenuItem
        column
        col={2}
        alignItem="center"
        justifyContent="flex-start"
        displayFlex
        selected={isSelected(ROUTE_DISCOVER_INVESTING_IDEAS)}
      >
        <RouterLink
          inheritColor
          noUnderline
          to={{
            type: ROUTE_DISCOVER_INVESTING_IDEAS,
          }}
          onClick={() => {
            trackEvent({
              type: 'track',
              subject: 'primaryNavMobile',
              modifier: 'Discover',
              action: 'click',
            });
          }}
        >
          <p>
            {isSelected(ROUTE_DISCOVER_INVESTING_IDEAS) ? (
              <DiscoverSolid />
            ) : (
              <DiscoverOutline />
            )}
          </p>
          <span>{t('app.topbar.nav.discover')}</span>
        </RouterLink>
      </MobileFooterMenuItem>
      <MobileFooterMenuItem
        column
        col={2}
        alignItem="center"
        justifyContent="flex-start"
        displayFlex
        selected={isSelected(ROUTE_WATCHLIST)}
      >
        <RouterLink
          inheritColor
          noUnderline
          to={{
            type: ROUTE_WATCHLIST,
          }}
          onClick={() => {
            trackEvent({
              type: 'track',
              subject: 'primaryNavMobile',
              modifier: 'Watchlist',
              action: 'click',
            });
          }}
        >
          <p>
            {isSelected(ROUTE_WATCHLIST) ? (
              <WatchlistSolid />
            ) : (
              <WatchlistOutline />
            )}
          </p>
          <span>{t('app.topbar.nav.watchlist')}</span>
        </RouterLink>
      </MobileFooterMenuItem>
      <MobileFooterMenuItem
        column
        col={2}
        alignItem="center"
        justifyContent="flex-start"
        displayFlex
        selected={isSelected(ROUTE_DEPRECATED_PORTFOLIO_LIST)}
      >
        <RouterLink
          inheritColor
          noUnderline
          to={{
            type: ROUTE_DEPRECATED_PORTFOLIO_LIST,
          }}
          onClick={() => {
            trackEvent({
              type: 'track',
              subject: 'primaryNavMobile',
              modifier: 'Portfolios',
              action: 'click',
            });
          }}
        >
          <p>
            {isSelected(ROUTE_DEPRECATED_PORTFOLIO_LIST) ? (
              <PortfolioSolid />
            ) : (
              <PortfolioOutline />
            )}
          </p>
          <span>{t('app.topbar.nav.portfolios')}</span>
        </RouterLink>
      </MobileFooterMenuItem>
      <MobileFooterMenuItem
        column
        col={2}
        alignItem="center"
        justifyContent="flex-start"
        displayFlex
        selected={isSelected(ROUTE_CREATE_CRITERIA)}
      >
        <RouterLink
          inheritColor
          noUnderline
          to={{
            type: ROUTE_CREATE_CRITERIA,
          }}
          onClick={() => {
            trackEvent({
              type: 'track',
              subject: 'primaryNavMobile',
              modifier: 'Screener',
              action: 'click',
            });
          }}
        >
          <p>
            {isSelected(ROUTE_CREATE_CRITERIA) ? (
              <ScreenerSolid />
            ) : (
              <ScreenerOutline />
            )}
          </p>
          <span>{t('app.topbar.nav.screener')}</span>
        </RouterLink>
      </MobileFooterMenuItem>
    </MobileFooterMenuWrapper>
  );
};
